import { Box, HStack, SegmentGroup as SG, VisuallyHidden } from '@chakra-ui/react';
import React from 'react';
import { SEGMENTED_CONTROL_INDICATOR_SHADOW } from '@/components/ui/shadows.ts';

export interface SegmentGroupOption {
	label: string;
	value: string;
	/** Displays a compact status marker without changing the option label. */
	isDirty?: boolean;
}

export interface SegmentGroupProps {
	/** Array of options to display */
	options: SegmentGroupOption[];
	/** Currently selected value */
	value: string | null;
	/** Callback when selection changes */
	onValueChange: (details: { value: string | null }) => void;
	/** Default selected value */
	defaultValue?: string | null;
	/** Size of the segment group */
	size?: 'sm' | 'md' | 'lg';
	/** Custom className for styling */
	className?: string;
	/** Full width */
	w?: string;
	/**
	 * The selected-indicator background, for a host whose own surface needs different contrast.
	 *
	 * Defaulted rather than left to the recipe, which gives the indicator no background at all: every
	 * caller but one relied on that default and drew a selected option that looked exactly like an
	 * unselected one.
	 */
	indicatorBg?: string;
	/**
	 * Overrides the label size. The segment-group recipe fixes item text at `sm` in its base
	 * styles rather than per size, so `size` alone cannot change it.
	 */
	fontSize?: string;
	/** Overrides the label weight, which the recipe leaves to inheritance. */
	fontWeight?: string;
	/**
	 * Keeps each label on one line. Off by default: some full-width groups rely on long labels
	 * wrapping on a narrow screen.
	 */
	noWrap?: boolean;
}

/**
 * Reusable segment group component for displaying radio-style options
 */
export const SegmentGroup: React.FC<SegmentGroupProps> = ({
	options,
	value,
	onValueChange,
	defaultValue,
	size = 'lg',
	className = 'segment-group',
	w = 'full',
	indicatorBg = 'surface.default',
	fontSize,
	fontWeight,
	noWrap = false,
}) => {
	return (
		<SG.Root
			rounded="3xl"
			defaultValue={defaultValue}
			value={value}
			onValueChange={onValueChange}
			size={size}
			w={w}
			whiteSpace={noWrap ? 'nowrap' : undefined}
			className={className}
		>
			{options.map((option) => (
				<SG.Item
					w="100%"
					key={option.value}
					value={option.value}
					cursor="pointer"
					fontSize={fontSize}
					fontWeight={fontWeight}
				>
					<SG.ItemText>
						<HStack as="span" gap={1.5}>
							<Box as="span">{option.label}</Box>
							{option.isDirty ? (
								<>
									<Box
										as="span"
										aria-hidden
										boxSize={2}
										borderRadius="full"
										bg="brand.700"
										flexShrink={0}
									/>
									<VisuallyHidden> — unsaved draft</VisuallyHidden>
								</>
							) : null}
						</HStack>
					</SG.ItemText>
					<SG.ItemHiddenInput />
				</SG.Item>
			))}
			<SG.Indicator px={0} rounded="3xl" bg={indicatorBg} boxShadow={SEGMENTED_CONTROL_INDICATOR_SHADOW} />
		</SG.Root>
	);
};
